import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

/**
 * TINA Badge / StatusPill — semantic tones from MASTER.md.
 *
 * Per the design rule "color is never the only signal", pair tones with
 * an icon when used for status. Plain badges (counts, labels) need no icon.
 */
const badgeVariants = cva(
  "inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium",
  {
    variants: {
      tone: {
        // Semantic tones
        primary: "bg-primary-subtle text-primary",
        success: "bg-success-subtle text-success",
        warning: "bg-warning-subtle text-warning",
        danger: "bg-danger-subtle text-danger",
        info: "bg-info-subtle text-info",
        accent: "bg-accent-subtle text-accent", // RESERVED: filing deadlines only
        neutral: "bg-surface-alt text-slate-700",
        // Solid variants for emphasis (use sparingly)
        "primary-solid": "bg-primary text-primary-fg",
        "danger-solid": "bg-danger text-white",
      },
    },
    defaultVariants: { tone: "neutral" },
  }
);

export interface BadgeProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof badgeVariants> {}

function Badge({ className, tone, ...props }: BadgeProps) {
  return <span className={cn(badgeVariants({ tone }), className)} {...props} />;
}

export { Badge, badgeVariants };
